import { Inject, Injectable, Injector } from '@angular/core';
import { assert } from '@sama/js-core';
import { cache } from '@sama/rxjs-core';
import { isUndefined } from 'lodash';
import {
  BehaviorSubject,
  firstValueFrom,
  fromEvent,
  merge,
  Observable,
  shareReplay,
  switchMap,
  timer,
} from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { Dimensions } from '../../cross-team-shared/types/misc';
import {
  addPoints,
  eventToPoint2D,
  mapPoint,
  midPoint,
  scalePoint,
  subtractPoints,
} from '../../shared/misc-utilities/point-utils';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../team-apis/core/shell-connector';
import { Point2D } from '../../team-apis/otterdam/answers/shape-typings';
import { AbstractSensorContentStore2D } from './state/abstract-sensor-content-store-2d';
import { AbstractKeyboardMovementService } from './utils/abstract-keyboard-movement-service';

interface SvgContainer {
  svg: Element;
  foreignObject: Element;
}

/**
 * We use 3 coordinate systems for `x,y`. This service translates between them.
 *
 * **Image coordinates:**
 * This is the most common system we use. This is what shape points are in. If
 * a variable name does not specify the system, assume this one.
 *
 * - Measured in the number of pixels in the image (so as you zoom in, it gets
 *   bigger on the screen).
 * - 0,0 is the top left of the image (so it moves on the screen as you pan the
 *   canvas)
 *
 * **Client coordinates:**
 * This is the clientX and clientY defined by the browser, e.g. in mouse events.
 * We quickly translate it into our other systems.
 *
 * - Measured in screen pixels
 * - 0,0 is the top left of the page (so moves on your screen as you scroll the
 *   page)
 *
 * **Container coordinates:**
 * Used for a couple less common cases. E.g. when a drag handler wants to know
 * how far the user moved their mouse, regardless of zoom level.
 *
 * - Measured in screen pixels
 * - 0,0 is the top left of the image (so it moves on the screen as you pan the
 *   canvas)
 *
 * **Pixel Size and Precision:**
 * An image is made up of a grid of pixel values; i.e., vector renderer works in a discrete space.
 * The smallest annotation precision is determined by the pixel size. If we set the pixel size
 * to 1, then we subdivide the image to match the width and height of the image. If we set the
 * pixel size to 0.1, then we subdivide the image by 10. The smaller the pixel size, the more
 * precise the annotation can be. This is important for orthographic projections where we need
 * more precision. For images or video, there is no need to subdivide the pixel.
 */
@Injectable()
export class MouseCoordinateService extends AbstractKeyboardMovementService {
  private svgElement$ = new BehaviorSubject<SvgContainer | undefined>(
    undefined,
  );
  private size?: Dimensions;
  private svgRendered$ = this.buildSvgRendered$();

  constructor(
    private store: AbstractSensorContentStore2D,
    @Inject(shellConnectorToken) private shellConnector: ShellConnector,
    injector: Injector,
  ) {
    super(injector);
  }

  async update(clientPoint = this.store.state().mouse.clientPoint) {
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!this.store.state().sensorRendererState || !this.svgElement$.value) {
      return; // still initializing
    }

    const containerPoint =
      await this.getContainerPointFromClientPoint(clientPoint);
    const imagePoint = this.getImagePointFromContainerPoint(containerPoint);
    this.store('mouse').set({ clientPoint, containerPoint, imagePoint });
    this.shellConnector.setMousePosition({
      clientPoint,
      imagePoint,
    });
  }

  updateOnMouseMove$(stopListening$: Observable<unknown>) {
    return fromEvent<MouseEvent>(document, 'mousemove').pipe(
      map((mouseMoveEvent) => eventToPoint2D(mouseMoveEvent)),
      tap(this.update.bind(this)),
      takeUntil(stopListening$),
    );
  }

  registerSvgElement(svgElement: SvgContainer) {
    this.svgElement$.next(svgElement);
  }

  getContainerPointFromImagePoint(imagePoint: Point2D) {
    return scalePoint(imagePoint, this.store.state().scale);
  }

  async getClientPointFromImagePoint(imagePoint: Point2D) {
    const containerPoint = this.getContainerPointFromImagePoint(imagePoint);
    return this.getClientPointFromContainerPoint(containerPoint);
  }

  async getImagePointFromClientPoint(clientPoint: Point2D) {
    const svgElement = await this.waitForSvgElement();

    // Get the top-left corner of the renderer in client coordinates
    const { left, top } = svgElement.foreignObject.getBoundingClientRect();
    // Convert to container coordinates
    const containerPoint = subtractPoints(clientPoint, [left, top]);

    return this.getImagePointFromContainerPoint(containerPoint);
  }

  getImagePointFromContainerPoint(containerPoint: Point2D) {
    const state = this.store.state();

    // Scale to match the units used for image coordinates. We used the width
    // but could have used height as well (zoom affects both equally).
    const scale = 1 / state.scale;
    const scaledPoint = scalePoint(containerPoint, scale);

    return mapPoint(
      scaledPoint,
      this.store.floorWithPixelSize.bind(this.store),
    );
  }

  async getImagePointInCenterOfSvg() {
    const svgElement = await this.waitForSvgElement();
    const { top, left, bottom, right } = svgElement.svg.getBoundingClientRect();
    const center = midPoint([
      [left, top],
      [right, bottom],
    ]);
    return this.getImagePointFromClientPoint(center);
  }

  handleViewBoxUpdate() {
    this.store('scale').set(this.getScaleFromAspectRatio());
    void this.update();
  }

  async getSvgElementSize(): Promise<Point2D> {
    const svgElement = await this.waitForSvgElement();
    const rect = svgElement.svg.getBoundingClientRect();
    return [rect.width, rect.height];
  }

  updateScale(size: Dimensions) {
    if (size.height === 0 && size.width === 0) {
      return;
    }
    this.size = size;
    this.store('scale').set(this.getScaleFromAspectRatio());
  }

  getScaleFromAspectRatio(
    size = this.size,
    viewBox = this.store.state().viewBox,
  ) {
    assert(
      size,
      'The renderer should always have a size when we are updating the scale ',
    );
    const { width, height } = size;
    // If the renderer is not initialized yet, out scale will be 1/0.
    if (width === 0 && height === 0) {
      return 1;
    }
    if (viewBox[2] / viewBox[3] < width / height) {
      return height / viewBox[3];
    }
    return width / viewBox[2];
  }

  private async getContainerPointFromClientPoint(clientPoint: Point2D) {
    const svgElement = await this.waitForSvgElement();
    const rect = svgElement.foreignObject.getBoundingClientRect();
    return subtractPoints(clientPoint, [rect.left, rect.top]);
  }

  private async getClientPointFromContainerPoint(containerPoint: Point2D) {
    return addPoints(containerPoint, await this.getContainerPosition());
  }

  private async getContainerPosition(): Promise<Point2D> {
    const svgElement = await this.waitForSvgElement();
    const rect = svgElement.foreignObject.getBoundingClientRect();
    return [rect.left, rect.top];
  }

  private async waitForSvgElement() {
    // svg element is used for calculation by calling getBoundingClientRect on
    // it. values will not be up-to-date with the previous pan/zoom if the
    // animation didn't finish yet, svgRendered$ ensures it is using delay(0)
    await Promise.all([
      firstValueFrom(
        this.svgElement$.pipe(
          filter((svgElement) => {
            if (isUndefined(svgElement)) {
              return false;
            }
            const { width, height } = svgElement.svg.getBoundingClientRect();
            return width > 0 && height > 0;
          }),
        ),
      ),
      firstValueFrom(
        this.svgRendered$.pipe(filter((isRendered) => isRendered)),
      ),
    ]);
    return this.svgElement$.value!;
  }

  private buildSvgRendered$() {
    const rendering$ = this.store('viewBox').$.pipe(
      map(() => false),
      cache(),
    );
    return merge(
      rendering$,
      rendering$.pipe(switchMap(() => timer(0).pipe(map(() => true)))),
    ).pipe(
      takeUntil(this.destruction$),
      shareReplay({ refCount: false, bufferSize: 1 }),
    );
  }
}
